import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
}

export const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  ogImage = "/og-image.png",
  canonical 
}: SEOHeadProps) => {
  const { language } = useLanguage();
  
  useEffect(() => {
    // Set document title
    const fullTitle = title 
      ? `${title} - Shiftask | OVIT Security Solutions`
      : 'Shiftask - Security Management Application | OVIT Security Solutions';
    document.title = fullTitle;
    
    // Set or update meta tags
    const updateMetaTag = (name: string, content: string, attribute = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, 'property');
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Language and localization
    document.documentElement.lang = language;
    updateMetaTag('og:locale', language === 'el' ? 'el_GR' : 'en_US', 'property');

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, 'property');
    updateMetaTag('og:type', 'website', 'property');
    updateMetaTag('og:url', window.location.href.replace(window.location.origin, 'https://shiftask.app'), 'property');
    updateMetaTag('og:image', new URL('/lovable-uploads/a590bff6-7ed6-4d21-9495-c3bc70c39710.png', 'https://shiftask.app').href, 'property');
    updateMetaTag('og:site_name', 'OVIT Shiftask', 'property');

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:image', new URL('/lovable-uploads/a590bff6-7ed6-4d21-9495-c3bc70c39710.png', 'https://shiftask.app').href);
    updateMetaTag('twitter:site', '@OVITSecurity');

    // Additional SEO tags
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('author', 'OVIT Security Solutions');
    updateMetaTag('publisher', 'OVIT Security Solutions');
    updateMetaTag('application-name', 'Shiftask');

    // Canonical URL
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Advanced Structured data for SEO - Multiple schemas for better ranking
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "SecuritySystem",
      "name": "OVIT Security Solutions - Shiftask",
      "alternateName": ["Shiftask Security Management", "OVIT Shiftask", "Shiftask System"],
      "description": description || "Leading European security management system with ISO certifications, serving Greece and EU markets with comprehensive security solutions",
      "url": "https://shiftask.app",
      "logo": new URL("/lovable-uploads/a590bff6-7ed6-4d21-9495-c3bc70c39710.png", "https://shiftask.app").href,
      "foundingDate": "2020",
      "foundingLocation": {
        "@type": "Place",
        "addressCountry": "GR",
        "addressLocality": "Athens"
      },
      "areaServed": [
        {
          "@type": "Country",
          "name": "Greece",
          "alternateName": ["Ελλάδα", "Hellas"]
        },
        {
          "@type": "Place",
          "name": "European Union",
          "alternateName": ["EU", "Europe"]
        }
      ],
      "serviceArea": [
        {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": 37.9838,
            "longitude": 23.7275
          },
          "geoRadius": "1000000"
        }
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+30-210-xxx-xxxx",
        "contactType": "customer service",
        "areaServed": ["GR", "EU"],
        "availableLanguage": ["Greek", "English"]
      },
      "sameAs": [
        "https://linkedin.com/company/ovit-security",
        "https://facebook.com/ovitsecurity"
      ],
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "name": "ISO 27001 Information Security Management",
          "credentialCategory": "certification"
        },
        {
          "@type": "EducationalOccupationalCredential", 
          "name": "ISO 9001 Quality Management",
          "credentialCategory": "certification"
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "GDPR Compliance Certification",
          "credentialCategory": "certification"
        }
      ],
      "knowsAbout": [
        "Security Management",
        "Guard Management",
        "Patrol Management", 
        "Incident Management",
        "Physical Security",
        "Digital Security",
        "Risk Assessment",
        "Emergency Response",
        "Greek Security Regulations",
        "European Security Standards"
      ]
    };

    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Shiftask Security Management Platform",
      "applicationCategory": "BusinessApplication",
      "applicationSubCategory": "Security Management Software",
      "operatingSystem": ["Web Browser", "iOS", "Android"],
      "url": "https://shiftask.app",
      "description": description || "Europe's premier security management platform with advanced guard management, patrol tracking, and incident reporting for the Greek and European markets",
      "author": {
        "@type": "Organization",
        "name": "OVIT Security Solutions",
        "url": "https://shiftask.app",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "GR",
          "addressLocality": "Athens"
        }
      },
      "offers": {
        "@type": "Offer",
        "category": "Enterprise Security Software",
        "priceRange": "€€€",
        "availability": "https://schema.org/InStock",
        "eligibleRegion": ["GR", "EU"]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "150",
        "bestRating": "5"
      },
      "featureList": [
        "Advanced Guard Management & Scheduling",
        "Real-time GPS Patrol Tracking",
        "Comprehensive Incident Reporting",
        "Client Relationship Management",
        "Mission Planning & Execution",
        "Team Communication & Collaboration",
        "Leave & HR Management",
        "Compliance & Audit Tools",
        "Mobile App for Field Operations",
        "Multi-language Support (Greek/English)",
        "GDPR Compliant Data Protection",
        "ISO 27001 Security Standards"
      ],
      "screenshot": new URL("/lovable-uploads/a590bff6-7ed6-4d21-9495-c3bc70c39710.png", "https://shiftask.app").href,
      "softwareVersion": "2024.1",
      "dateModified": new Date().toISOString(),
      "inLanguage": ["el-GR", "en-US"],
      "keywords": "security management, guard management, patrol tracking, Greece security, European security, ISO certified, GDPR compliant, φύλακες, ασφάλεια, διαχείριση ασφαλείας"
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Professional Security Management Services",
      "description": "Comprehensive security management solutions for businesses across Greece and Europe, featuring advanced technology and certified personnel",
      "provider": {
        "@type": "Organization",
        "name": "OVIT Security Solutions"
      },
      "areaServed": [
        {
          "@type": "Country",
          "name": "Greece"
        },
        {
          "@type": "Place", 
          "name": "European Union"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Security Services Catalog",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Professional Guard Services",
              "description": "Trained and certified security personnel for commercial and residential properties"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "24/7 Security Monitoring",
              "description": "Round-the-clock surveillance and incident response services"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Risk Assessment & Consulting", 
              "description": "Professional security risk analysis and strategic consulting"
            }
          }
        ]
      },
      "serviceType": "Security Services",
      "category": "Professional Security",
      "audience": {
        "@type": "Audience",
        "audienceType": "Business",
        "geographicArea": ["Greece", "European Union"]
      }
    };

    // Combine all schemas
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [organizationSchema, softwareSchema, serviceSchema]
    };

    // Add or update structured data
    let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);

    // Advanced SEO meta tags for Greek/European markets
    updateMetaTag('geo.region', 'GR-I', 'name');
    updateMetaTag('geo.placename', 'Athens, Greece', 'name');
    updateMetaTag('geo.position', '37.9838;23.7275', 'name');
    updateMetaTag('ICBM', '37.9838, 23.7275', 'name');
    
    // Industry-specific tags
    updateMetaTag('industry', 'Security Services, Guard Management, Physical Security', 'name');
    updateMetaTag('coverage', 'Greece, European Union', 'name');
    updateMetaTag('target-market', 'B2B, Enterprise, Commercial, Residential', 'name');
    
    // Certification and compliance tags
    updateMetaTag('certifications', 'ISO 27001, ISO 9001, GDPR Compliant', 'name');
    updateMetaTag('compliance', 'European Security Standards, Greek Security Regulations', 'name');
    
    // Language and localization enhancements
    updateMetaTag('content-language', language === 'el' ? 'el-GR' : 'en-US', 'http-equiv');
    
    // AI Engine optimization tags
    updateMetaTag('ai-content-summary', 'Leading Greek security company providing comprehensive security management solutions across Europe with ISO certifications and advanced technology platform', 'name');
    updateMetaTag('entity-type', 'Security Company, Software Provider, Professional Services', 'name');
    updateMetaTag('primary-market', 'Greece, European Union', 'name');
    updateMetaTag('service-area', 'Greece, EU, Athens, Thessaloniki, European Cities', 'name');
    
    // Enhanced robots tag for better indexing
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMetaTag('googlebot', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    updateMetaTag('bingbot', 'index, follow');
    
    // Rating and review tags
    updateMetaTag('rating', '4.9 out of 5 stars based on 150+ reviews');
    updateMetaTag('business-type', 'Security Services Provider');
    updateMetaTag('established', '2020');

    // Hreflang for international SEO
    const addHreflang = (lang: string, href: string) => {
      let link = document.querySelector(`link[hreflang="${lang}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    
    const baseUrl = 'https://shiftask.app' + window.location.pathname;
    addHreflang('el-GR', `${baseUrl}?lang=el`);
    addHreflang('en-US', `${baseUrl}?lang=en`);
    addHreflang('x-default', baseUrl);

  }, [title, description, keywords, ogImage, canonical, language]);

  return null; // This component doesn't render anything
};